import useTranslation from "next-translate/useTranslation";
import {
  ChessTerm,
  ChessTermType,
  Game,
  GameReviewPosition,
  MoveType,
} from "@chesshotel/types";
import { getKeyAndValues } from "./translatedComments";
import Trans from "next-translate/Trans";
import CommentTerm from "./CommentTerm";
import Translation from "./Translation";
import MoveIcon from "@/components/common/move-icon";
import { BLACK, Color, Move, WHITE } from "@chesshotel/chess";
import { renderSanWithSvg } from "../sanUtils";
import { cn } from "@/lib/utils";
import { Translate } from "next-translate";
import { Button } from "@/components/ui/button";

// Map move types to translation keys
const moveTypeToTranslationKey: Record<string, string> = {
  [MoveType.MISTAKE]: "is_a_mistake",
  [MoveType.GOOD]: "is_a_good_move",
  [MoveType.BEST]: "is_the_best_move",
  [MoveType.BOOK]: "is_a_book_move",
  [MoveType.BLUNDER]: "is_a_blunder",
  [MoveType.INACCURACY]: "is_a_inaccuracy",
  [MoveType.BRILLIANT]: "is_a_brilliant_move",
  [MoveType.GREAT]: "is_a_great_move",
  [MoveType.EXCELLENT]: "is_a_excellent_move",
};

// Helper function to check if a move is the last book move
function isLastBookMove(
  position: GameReviewPosition,
  gameReview: GameReviewPosition[],
  moveIndex: number
): boolean {
  if (!gameReview) return false;
  if (position.moveType !== MoveType.BOOK || !position.opening) {
    return false;
  }

  // Check if there's any move after this one that has an opening
  for (let i = moveIndex + 1; i < gameReview.length; i++) {
    if (gameReview[i].opening) {
      return false;
    }
  }

  return true;
}

// --- ScoreDisplay component ---
function ScoreDisplay({
  score,
  mateInMoves,
  moveIndex,
}: {
  score: number;
  mateInMoves?: number;
  moveIndex: number;
}) {
  if (typeof mateInMoves === "number" && !isNaN(mateInMoves)) {
    const isWhiteTurn = moveIndex % 2 === 0;

    const isWhiteMate =
      (isWhiteTurn && mateInMoves > 0) || (!isWhiteTurn && mateInMoves < 0);
    const bg = isWhiteMate ? "bg-athens-gray-100" : "bg-gray-700";
    const text = isWhiteMate ? "text-gray-900" : "text-white";
    const sign = isWhiteMate ? "+" : "-";
    return (
      <div
        className={`px-2 py-0.5 rounded text-sm font-bold ${bg} ${text}`}
        style={{ minWidth: 44, textAlign: "center" }}
        aria-label="Engine mate in moves"
      >
        {`${sign}M${Math.abs(mateInMoves)}`}
      </div>
    );
  }
  if (typeof score !== "number" || isNaN(score)) return null;
  const isWhiteAhead = score >= 0;
  const bg = isWhiteAhead ? "bg-athens-gray-100" : "bg-gray-700";
  const text = isWhiteAhead ? "text-gray-900" : "text-white";
  const sign = score > 0 ? "+" : score < 0 ? "-" : "±";
  // Always show sign, two decimals
  const absScore = Math.abs(score).toFixed(2);
  return (
    <div
      className={`px-2 py-0.5 rounded text-sm font-bold ${bg} ${text}`}
      style={{ minWidth: 44, textAlign: "center" }}
      aria-label="Engine evaluation score"
    >
      {score === 0 ? "0.00" : `${sign}${absScore}`}
    </div>
  );
}
// --- End ScoreDisplay ---

function ChessTerms({
  chessTerms,
  moveType,
  opening,
  t,
  fen,
  onChessTermClick,
  onChessTermOver,
  onChessTermOut,
  isLastBookMove = false,
}: {
  chessTerms: ChessTerm[];
  moveType: string;
  opening: string;
  t: Translate;
  fen: string;
  onChessTermClick: () => void;
  onChessTermOver: (chessTerm: ChessTerm) => void;
  onChessTermOut: (chessTerm: ChessTerm) => void;
  isLastBookMove: boolean;
}) {
  if (moveType == MoveType.BOOK) {
    return (
      <div>
        {opening}
        {isLastBookMove && <span>. {t("is_the_last_book_move")}</span>}
      </div>
    );
  }

  return (
    <>
      {moveType == MoveType.GREAT && <span>{t("only_good_move")} </span>}
      {}
      {chessTerms?.map((chessTerm, index) => {
        const { i18nKey, values } = getKeyAndValues(
          chessTerm,
          moveType,
          t,
          fen
        );

        if (!i18nKey) {
          console.log("missed chess term", chessTerm);
          return null;
        }

        return (
          <span
            className="inline-block first-letter:uppercase"
            key={chessTerm.term + index}
          >
            <Trans
              key={chessTerm.term + index}
              i18nKey={i18nKey}
              values={values}
              components={[
                <CommentTerm
                  chessTerm={chessTerm}
                  onChessTermOver={onChessTermOver}
                  onChessTermOut={onChessTermOut}
                  onChessTermClick={onChessTermClick}
                />,
              ]}
              ns="gameReview"
            />{" "}
            {/* <Translation
              chessTerm={chessTerm}
              i18nKey={i18nKey}
              values={values}
              onChessTermClick={onChessTermClick}
              onChessTermOver={onChessTermOver}
              onChessTermOut={onChessTermOut}
            /> */}
            {index !== chessTerms.length - 1 && <span> </span>}
          </span>
        );
      })}
    </>
  );
}

export default function Comments({
  isGuest,
  position,
  positionIndex,
  gameReview,
  introTitle,
  introText,
  onChessTermClick,
  onChessTermOver,
  onChessTermOut,
  children,
  requireSignup,
  previewCap,
}: {
  isGuest: boolean;
  introTitle?: string;
  introText?: string;
  position: GameReviewPosition;
  positionIndex: number;
  gameReview?: GameReviewPosition[];
  onChessTermClick: () => void;
  onChessTermOver: (chessTerm: ChessTerm) => void;
  onChessTermOut: (chessTerm: ChessTerm) => void;
  children?: React.ReactNode;
  requireSignup?: () => void;
  previewCap?: number;
}) {
  const { t } = useTranslation("gameReview");

  if (positionIndex == 0) {
    return (
      <div className="overflow-scroll relative p-3 pr-3 w-full h-auto text-sm leading-normal text-gray-900 bg-white rounded-md md:p-4 md:h-[164px] min-h-[140px] md:text-[15px]">
        <div className="flex justify-between items-center pb-3">
          <div className="font-semibold">{t("Welcome to Game Review!")}</div>
          {position && (
            <ScoreDisplay
              score={position.score}
              mateInMoves={position.mateInMoves}
              moveIndex={position.moveIndex}
            />
          )}
        </div>

        <div>
          {t(
            "Click the Next-button to jump to the first key move of the game."
          )}
        </div>
      </div>
    );
  }

  if (!position) return null;

  if (isGuest && previewCap && positionIndex > previewCap) {
    return (
      <div className="overflow-scroll relative p-3 pr-3 w-full h-auto text-sm leading-normal text-gray-900 bg-white rounded-md md:p-4 md:h-[164px] min-h-[140px] md:text-[15px]">
        <div className="flex justify-between items-center pb-3">
          <div className="font-semibold">{t("Unlock full Game Review")}</div>
        </div>
        <div>
          {t("Sign up to see comments and best moves for every position.")}
        </div>
        <Button className="mt-3" onClick={requireSignup ?? (() => {})}>
          {t("Sign up free")}
        </Button>
      </div>
    );
  }

  const { chessTerms, moveType, move, score, opening, san, moveIndex, fen } =
    position;
  const moveColor = moveIndex % 2 === 1 ? WHITE : BLACK;

  return (
    <div
      className={cn(
        "overflow-scroll relative p-3 pr-3 w-full h-auto text-sm leading-normal text-gray-900 bg-white rounded-md md:p-4 md:h-[164px] min-h-[140px] md:text-[15px]"
      )}
    >
      {/* Title row with MoveIcon, title, and score display */}
      <div className="flex justify-between items-center pb-3">
        <CommentTitle
          {...{ position, gameReview, moveType, move, score, san, moveColor }}
        />
        <ScoreDisplay
          score={score}
          mateInMoves={position.mateInMoves}
          moveIndex={moveIndex}
        />
      </div>

      <ChessTerms
        chessTerms={chessTerms}
        moveType={moveType}
        opening={opening}
        t={t}
        fen={fen}
        onChessTermClick={onChessTermClick}
        onChessTermOver={onChessTermOver}
        onChessTermOut={onChessTermOut}
        isLastBookMove={isLastBookMove(
          position,
          gameReview,
          position.moveIndex
        )}
      />
      {/* <span>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
        tempor incididunt ut labore et.
      </span> */}

      {children && (
        <div className="">
          {/* Overlay Show idea button if present */}
          <div className="absolute right-3 bottom-3 z-10 md:left-4 md:bottom-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

function CommentTitle({
  position,
  gameReview,
  moveType,
  san,
  moveColor,
}: {
  position: GameReviewPosition;
  gameReview?: GameReviewPosition[];
  moveType: string;
  san: string;
  moveColor: Color;
}) {
  const { t } = useTranslation("gameReview");

  let moveTypeDescription = "";
  if (moveType) {
    // Check if this is the last book move
    // if (
    //   moveType === MoveType.BOOK &&
    //   gameReview &&
    //   isLastBookMove(position, gameReview, position.moveIndex)
    // ) {
    //   moveTypeDescription = t("is_the_last_book_move");
    // } else {
    moveTypeDescription = t(moveTypeToTranslationKey[moveType]);
    // }
  }

  return (
    <div className="flex">
      <div className="flex items-center space-x-1.5">
        <MoveIcon moveType={moveType} />
        <div className="font-semibold">
          {renderSanWithSvg(san, moveColor)} {moveTypeDescription}
        </div>
      </div>
    </div>
  );
}
